import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { MenubarModule } from 'primeng/menubar';
import { SkeletonModule } from 'primeng/skeleton';
import { filter } from 'rxjs/operators';
import { EntityType } from '../../../../api/enum/entity-type';
import { LegacyReviewComponent } from "../legacy-review/legacy-review.component";
import { ShareDataService } from '../../../../services';
import { Matter } from '../../../../api/data-model';

@Component({
  selector: 'app-sub-menubar',
  standalone: true,
  imports: [CommonModule, RouterModule, MenubarModule, SkeletonModule, LegacyReviewComponent],
  templateUrl: './sub-menubar.component.html',
  styleUrls: ['./sub-menubar.component.scss'],
})
export class SubMenubarComponent implements OnInit {
  menuItems: MenuItem[] = [];
  activeMenu: string | null = null;
  isLoading = false;

  entityType: number | null = null;
  entityCode: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly shareDataService: ShareDataService
  ) { }

  ngOnInit() {
    this.updateMenu();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.updateMenu();
    });
  }

  private updateMenu() {
    const url = this.router.url;

    // Traverse the route tree to find the deepest activated route
    let route = this.route.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }

    this.entityType = null;
    this.entityCode = route.params['code'];

    if (url.includes('/matter/')) {
      const code = route.params['code'];
      this.activeMenu = 'matter';
      this.entityType = EntityType.Matter;

      if (!code) {
        this.menuItems = [];
        this.isLoading = false;
      } else {
        this.isLoading = true;

        this.shareDataService.getMatterByCode(code).subscribe({
          next: (matter) => {
            this.menuItems = this.buildMatterMenuItems(code, matter);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Failed to load matter data for menu:', error);
            // Fallback to basic menu without Position if matter data fails
            this.menuItems = this.buildMatterMenuItems(code, null);
            this.isLoading = false;
          }
        });
      }
    } else if (url.includes('/company/')) {
      const code = route.params['code'];
      this.activeMenu = 'company';
      this.entityType = EntityType.Company;
      this.menuItems = !code ? [] : this.buildCompanyMenuItems(code);
      this.isLoading = false;
    } else if (url.includes('/foreign-national/')) {
      const code = route.params['code'];
      this.activeMenu = 'foreign-national';
      this.entityType = EntityType.ForeignNational;
      this.menuItems = !code ? [] : this.buildForeignNationalMenuItems(code);
      this.isLoading = false;
    } else if (url.includes('/admin/process-modules/')) {
      const id = route.params['id'];
      this.activeMenu = 'process-module';
      this.menuItems = this.buildProcessModuleMenuItems(id);
      this.isLoading = false;
    } else {
      this.activeMenu = null;
      this.menuItems = [];
      this.isLoading = false;
    }
  }

  /**
   * Build matter menu items based on matter data.
   * Only shows Position menu item if processModuleName is 'PERM'.
   */
  private buildMatterMenuItems(code: string, matter: Matter | null): MenuItem[] {
    const menuItems: MenuItem[] = [
      {
        label: 'Dashboard',
        routerLink: [`/matter/${code}/dashboard`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Details',
        routerLink: [`/matter/${code}/details`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Tasks',
        routerLink: [`/matter/${code}/tasks`],
      },
      {
        label: 'Workflow Steps',
        routerLink: [`/matter/${code}/workflow-steps`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Receipts',
        routerLink: [`/matter/${code}/receipts`],
        routerLinkActiveOptions: { exact: true },
      },
    ];

    // Only add Position menu item if processModuleName is 'PERM'
    if (matter && matter.processModuleName === 'PERM') {
      menuItems.push({
        label: 'Position',
        routerLink: [`/matter/${code}/position`],
        routerLinkActiveOptions: { exact: true },
      });
    }

    // Add remaining menu items
    menuItems.push(
      {
        label: 'Forms',
        routerLink: [`/matter/${code}/forms`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Letters',
        routerLink: [`/matter/${code}/letters`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Questionnaires',
        routerLink: [`/matter/${code}/questionnaires`],
      },
      {
        label: 'Documents',
        routerLink: [`/matter/${code}/requestedDocuments`],
        routerLinkActiveOptions: { exact: true },
      }
    );

    return menuItems;
  }

  /**
   * Build company menu items.
   */
  private buildCompanyMenuItems(code: string): MenuItem[] {
    return [
      {
        label: 'Dashboard',
        routerLink: [`/company/${code}/dashboard`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Profile',
        routerLink: [`/company/${code}/profile`],
      },
      {
        label: 'Employees',
        routerLink: [`/company/${code}/employees`],
      },
      {
        label: 'Users',
        routerLink: [`/company/${code}/users`],
      },
      {
        label: 'Locations',
        routerLink: [`/company/${code}/locations`],
      },
      {
        label: 'PERM',
        items: [
          {
            label: 'Positions',
            routerLink: [`/company/${code}/positions`],
          },
          {
            label: 'Prevailing Wage',
            routerLink: [`/company/${code}/pws`],
          },
          {
            label: 'Recruiting',
            routerLink: [`/company/${code}/recruiting`],
          },
        ],
      },
    ];
  }

  /**
   * Build foreign national menu items.
   */
  private buildForeignNationalMenuItems(code: string): MenuItem[] {
    return [
      {
        label: 'Dashboard',
        routerLink: [`/foreign-national/${code}/dashboard`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Profile',
        routerLink: [`/foreign-national/${code}/profile`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Priority Dates',
        routerLink: [`/foreign-national/${code}/priority-dates`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'Document Expiration',
        routerLink: [`/foreign-national/${code}/document-expiration`],
        routerLinkActiveOptions: { exact: true },
      },
      {
        label: 'New Matter',
        routerLink: [`/foreign-national/${code}/new-matter`],
        routerLinkActiveOptions: { exact: true },
      }
      // {
      //   label: 'Forms',
      //   routerLink: [`/foreign-national/${code}/forms`],
      //   routerLinkActiveOptions: { exact: true },
      // },
      // {
      //   label: 'Letters',
      //   routerLink: [`/foreign-national/${code}/letters`],
      //   routerLinkActiveOptions: { exact: true },
      // },
      // {
      //   label: 'Questionnaires',
      //   routerLink: [`/foreign-national/${code}/questionnaires`],
      //   routerLinkActiveOptions: { exact: true },
      // }
    ];
  }

  /**
   * Build process module menu items.
   */
  private buildProcessModuleMenuItems(id: string): MenuItem[] {
    return [
      {
        label: 'Dashboard',
        routerLink: [`/admin/process-modules/${id}/dashboard`],
        routerLinkActiveOptions: { exact: true },
      },
    ];
  }
}
